import type { ControlPoint, Drawing, PathSegment } from '@/types'

/**
 * Get a point from a drawing by ID.
 * Returns the point from the shared point pool.
 */
export function getPoint(drawing: Drawing, pointId: string): ControlPoint | undefined {
	return drawing.points[pointId]
}

/**
 * Get all points for a segment (resolved from IDs).
 * Filters out undefined points if a referenced ID doesn't exist in the pool.
 */
export function getSegmentPoints(drawing: Drawing, segment: PathSegment): ControlPoint[] {
	return segment.pointIds
		.map((id) => drawing.points[id])
		.filter((point): point is ControlPoint => point !== undefined)
}

/**
 * Get the start point of a drawing (first point of first segment).
 */
export function getDrawingStartPoint(drawing: Drawing): ControlPoint | null {
	const firstSegment = drawing.segments[0]
	if (!firstSegment) return null
	const firstPointId = firstSegment.pointIds[0]
	if (!firstPointId) return null
	return drawing.points[firstPointId] ?? null
}

/**
 * Get the end point of a drawing (last point of last segment).
 */
export function getDrawingEndPoint(drawing: Drawing): ControlPoint | null {
	const lastSegment = drawing.segments[drawing.segments.length - 1]
	if (!lastSegment) return null
	const lastPointId = lastSegment.pointIds[lastSegment.pointIds.length - 1]
	if (!lastPointId) return null
	return drawing.points[lastPointId] ?? null
}

/**
 * Get ordered list of point IDs by traversing segments.
 */
export function getOrderedPointIds(drawing: Drawing): string[] {
	if (drawing.segments.length === 0) {
		return Object.keys(drawing.points)
	}

	const orderedIds: string[] = []
	const seen = new Set<string>()

	for (const segment of drawing.segments) {
		for (const pointId of segment.pointIds) {
			if (!seen.has(pointId)) {
				orderedIds.push(pointId)
				seen.add(pointId)
			}
		}
	}

	return orderedIds
}

/**
 * Get ordered points from a drawing by traversing segments
 */
export function getOrderedPointsFromDrawing(drawing: Drawing): ControlPoint[] {
	const orderedIds: string[] = []
	const seen = new Set<string>()

	for (const segment of drawing.segments) {
		for (const pointId of segment.pointIds) {
			if (!seen.has(pointId)) {
				orderedIds.push(pointId)
				seen.add(pointId)
			}
		}
	}

	return orderedIds.map(id => drawing.points[id]).filter(Boolean) as ControlPoint[]
}

/**
 * Check if a point is the start point of a drawing.
 */
export function isStartPoint(drawing: Drawing, pointId: string): boolean {
	const firstSegment = drawing.segments[0]
	if (!firstSegment) return false
	const firstPointId = firstSegment.pointIds[0]
	return firstPointId == pointId
}

/**
 * Check if a point is the end point of a drawing.
 */
export function isEndPoint(drawing: Drawing, pointId: string): boolean {
	const lastSegment = drawing.segments[drawing.segments.length - 1]
	if (!lastSegment) return false
	const lastPointId = lastSegment.pointIds[lastSegment.pointIds.length - 1]
	return lastPointId == pointId
}


